import { useEffect, useMemo, useState } from 'react';
import { subscribeToCoachSchedule, subscribeToCoachSchedulesMeta } from '../../coachSchedules/coachSchedulesService';
import { CoachScheduleDoc, CoachSchedulesMeta } from '../../coachSchedules/types';
import { LoadedClient, toDashboardClient } from '../tasks/clientRecord';
import { fetchClientsByIds } from '../tasks/taskSources';
import { DashboardClient } from '../tasks/types';
import { TASK_CACHE_MS } from '../tasks/useClientTasks';
import { LiveSchedule, LoadState } from './scheduleSource';

/**
 * How long a copy from the offline cache is held back while the server answers. A cached meta or
 * day from the last visit would otherwise flash an old schedule with a "late" warning; after this,
 * the cached copy is shown (offline), and its "Updated" line says how old it is.
 */
export const CACHE_GRACE_MS = 3000;
/** A failed client lookup is tried again after this long. */
export const LOOKUP_RETRY_MS = 30 * 1000;

interface Received<T> {
  status: LoadState;
  value: T;
  fromCache: boolean;
}

/** Ready only once the server has answered, or the grace period is over. */
const settled = (status: LoadState, fromCache: boolean, graceOver: boolean): LoadState =>
  status === 'ready' && fromCache && !graceOver ? 'loading' : status;

/**
 * Listens to the coach's schedule for two dates and to the sync meta: three document reads per
 * visit, then live updates after each run. New dates (at midnight) re-subscribe.
 */
export function useCoachSchedule(coachId: string, enabled: boolean, today: string, tomorrow: string): LiveSchedule {
  const [meta, setMeta] = useState<Received<CoachSchedulesMeta | null>>({ status: 'loading', value: null, fromCache: false });
  const [days, setDays] = useState<Record<string, Received<CoachScheduleDoc | null>>>({});
  const [graceOver, setGraceOver] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    setGraceOver(false);
    const timer = setTimeout(() => setGraceOver(true), CACHE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    setMeta({ status: 'loading', value: null, fromCache: false });
    return subscribeToCoachSchedulesMeta(
      (value, fromCache) => setMeta({ status: 'ready', value, fromCache }),
      () => setMeta({ status: 'error', value: null, fromCache: false })
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    // At midnight yesterday's "tomorrow" is today's day: keep it on screen while the new listener starts.
    setDays((current) => Object.fromEntries(Object.entries(current).filter(([date]) => date === today || date === tomorrow)));
    const unsubscribes = [today, tomorrow].map((date) =>
      subscribeToCoachSchedule(
        coachId,
        date,
        (schedule, fromCache) => setDays((current) => ({ ...current, [date]: { status: 'ready', value: schedule, fromCache } })),
        () => setDays((current) => ({ ...current, [date]: { status: 'error', value: null, fromCache: false } }))
      )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [enabled, coachId, today, tomorrow]);

  return useMemo(() => {
    const shown: LiveSchedule['days'] = {};
    Object.entries(days).forEach(([date, day]) => {
      shown[date] = { status: settled(day.status, day.fromCache, graceOver), schedule: day.value };
    });
    return { enabled, meta: { status: settled(meta.status, meta.fromCache, graceOver), value: meta.value }, days: shown };
  }, [enabled, meta, days, graceOver]);
}

/** Mirror records fetched by id; null marks an id the mirror doesn't hold. */
const cache = new Map<string, { loadedAt: number; client: LoadedClient | null }>();

const isFresh = (id: string) => {
  const entry = cache.get(id);
  return !!entry && Date.now() - entry.loadedAt < TASK_CACHE_MS;
};

export interface ScheduleClients {
  clients: DashboardClient[];
  /** Still fetching: a client shown without a record may yet turn out to have one. */
  pending: boolean;
  /** The last fetch failed (it is retried): whether those clients are in the list is unknown. */
  failed: boolean;
}

/**
 * The scheduled clients the dashboard hasn't loaded: led by another coach, or not in the client
 * list at all. Fetched one document each (a handful a day) once the coach's own lists have settled.
 */
export function useScheduleClients(ids: string[], ready: boolean): ScheduleClients {
  const key = ready ? Array.from(new Set(ids)).sort().join(',') : '';
  const [version, setVersion] = useState(0);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const missing = useMemo(() => (key ? key.split(',').filter((id) => !isFresh(id)) : []), [key, version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (missing.length === 0) return undefined;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    fetchClientsByIds(missing)
      .then((found) => {
        const byId = new Map(found.map((client) => [client.clientId, client]));
        const loadedAt = Date.now();
        missing.forEach((id) => cache.set(id, { loadedAt, client: byId.get(id) ?? null }));
        if (cancelled) return;
        setFailedKey(null);
        setVersion((n) => n + 1);
      })
      .catch((error) => {
        console.error('Dashboard: scheduled clients failed to load', error);
        if (cancelled) return;
        setFailedKey(key);
        // A new `missing` array re-runs this effect.
        retry = setTimeout(() => setVersion((n) => n + 1), LOOKUP_RETRY_MS);
      });
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [missing, key]);

  const clients = useMemo(() => {
    const now = new Date();
    return (key ? key.split(',') : [])
      .map((id) => cache.get(id)?.client)
      .filter((client): client is LoadedClient => !!client)
      .map((client) => toDashboardClient(client, now));
  }, [key, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const failed = failedKey !== null && failedKey === key;
  return { clients, pending: missing.length > 0 && !failed, failed };
}
